// The two set-a-password link pages (FR-039 to FR-045; SA-AUTH-1; P-AUTH-1):
// /reset-password (resetPassword) and /accept-invite (acceptInvite). Shared steps:
// - the one-time code is scrubbed from the address first and kept in memory (useLinkToken);
// - signed in: the interstitial first. Continue signs out on this device (LINK_PAGE, the pending
//   join is kept); Cancel goes home (FR-041);
// - new password and confirmation with the 8 to 128 rule (FR-005);
// - on success the outcome comes only from the server's allowedClients (inviteOutcome); the user is
//   never signed in automatically (FR-044);
// - a used, expired or damaged link: reset offers Send a new link (Forgot password); invite shows
//   the expired message with Log in (FR-040, FR-045).
import { useRouter } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";

import { isAppError } from "@/api/errors";
import { useSession, useSessionController } from "@/session/hooks";
import { makeStyles } from "@/theme/makeStyles";
import { useSubmitAction } from "@/lib/useSubmitAction";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { InlineMessage } from "@/ui/InlineMessage";
import { PasswordField } from "@/ui/PasswordField";
import { Text } from "@/ui/Text";

import { useAuthApi } from "../AuthApiContext";
import { AUTH_COPY } from "../copy";
import { type AuthErrorView, fieldMessage, messageFor } from "../messages";
import { AuthCard } from "../ui/AuthCard";
import { checkConfirmation, checkNewPassword } from "../validation";
import { inviteOutcome, type OutcomeView, outcomeView } from "./inviteOutcome";
import { SignedInInterstitial } from "./SignedInInterstitial";
import { useLinkToken } from "./useLinkToken";

export type PasswordLinkPage = "reset" | "invite";

type Banner = Exclude<AuthErrorView, { kind: "fields" | "linkInvalid" }>;

const useStyles = makeStyles((t) =>
  StyleSheet.create({ stack: { gap: t.space.lg } }),
);

export function PasswordLinkScreen({ page }: { page: PasswordLinkPage }) {
  const styles = useStyles();
  const authApi = useAuthApi();
  const router = useRouter();
  const session = useSession();
  const { signOut } = useSessionController();
  const token = useLinkToken();
  const copy = page === "reset" ? AUTH_COPY.reset : AUTH_COPY.invite;
  const operation = page === "reset" ? "resetPassword" : "acceptInvite";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const [banner, setBanner] = useState<Banner | null>(null);
  const [outcome, setOutcome] = useState<OutcomeView | null>(null);
  const [linkInvalid, setLinkInvalid] = useState(token === null);

  const setPasswordAction = useSubmitAction(async (pw: string) => {
    if (token === null) throw new Error("no link code");
    return page === "reset"
      ? authApi.resetPassword(token, pw)
      : authApi.acceptInvite(token, pw);
  });

  async function submit() {
    const p = checkNewPassword(password);
    const c = checkConfirmation(password, confirmation);
    setErrors({
      ...(p.ok ? {} : { password: fieldMessage("password", p.code) }),
      ...(c.ok
        ? {}
        : { confirmPassword: fieldMessage("confirmPassword", c.code) }),
    });
    if (!p.ok || !c.ok) return;
    setBanner(null);
    const result = await setPasswordAction.submit(p.value);
    if (result.status === "skipped") return;
    if (result.status === "done") {
      setOutcome(outcomeView(inviteOutcome(result.value.allowedClients), page));
      return;
    }
    if (!isAppError(result.error)) throw result.error;
    const view = messageFor(result.error, operation);
    if (view.kind === "linkInvalid") setLinkInvalid(true);
    // A field error keeps the link usable (the code is not spent).
    else if (view.kind === "fields")
      setErrors({ password: view.fields.password });
    else setBanner(view);
  }

  if (linkInvalid) {
    return (
      <AuthCard title={AUTH_COPY.reset.linkInvalid} testID={`${page}-invalid`}>
        <View style={styles.stack}>
          {page === "invite" ? (
            <Text>{AUTH_COPY.invite.linkInvalid}</Text>
          ) : null}
          {page === "reset" ? (
            <Button
              onPress={() => router.replace("/forgot-password")}
              testID="reset-new-link"
            >
              {AUTH_COPY.actions.sendNewLink}
            </Button>
          ) : (
            <Button
              onPress={() => router.replace("/login")}
              testID="invite-login"
            >
              {AUTH_COPY.actions.logIn}
            </Button>
          )}
        </View>
      </AuthCard>
    );
  }

  if (outcome) {
    return (
      <AuthCard title={copy.title} testID={`${page}-done`}>
        <View style={styles.stack}>
          <InlineMessage tone="success">{outcome.text}</InlineMessage>
          {outcome.showLogIn ? (
            <Button
              onPress={() => router.replace("/login")}
              testID={`${page}-done-login`}
            >
              {AUTH_COPY.actions.logIn}
            </Button>
          ) : null}
        </View>
      </AuthCard>
    );
  }

  if (session.status === "signedIn") {
    return (
      <SignedInInterstitial
        title={copy.title}
        name={session.user.displayName}
        onContinue={() => void signOut("LINK_PAGE")}
        onCancel={() => router.replace("/")}
      />
    );
  }

  return (
    <AuthCard title={copy.title} testID={`${page}-screen`}>
      {banner ? (
        <InlineMessage
          tone={banner.kind === "connection" ? "warning" : "error"}
          detail={
            banner.kind === "banner" && banner.reference
              ? AUTH_COPY.errors.reference(banner.reference)
              : null
          }
        >
          {banner.text}
        </InlineMessage>
      ) : null}
      <Form onSubmit={() => void submit()} accessibilityLabel={copy.title}>
        <View style={styles.stack}>
          <PasswordField
            label={
              page === "reset"
                ? AUTH_COPY.fields.newPassword
                : AUTH_COPY.fields.password
            }
            kind="new-password"
            value={password}
            onChangeText={setPassword}
            error={errors.password}
            hint={AUTH_COPY.fields.passwordRule}
            showLabel={AUTH_COPY.actions.showPassword}
            hideLabel={AUTH_COPY.actions.hidePassword}
            testID={`${page}-password`}
          />
          <PasswordField
            label={AUTH_COPY.fields.confirmPassword}
            kind="new-password"
            value={confirmation}
            onChangeText={setConfirmation}
            error={errors.confirmPassword}
            showLabel={AUTH_COPY.actions.showPassword}
            hideLabel={AUTH_COPY.actions.hidePassword}
            testID={`${page}-confirm`}
          />
          <Button
            onPress={() => void submit()}
            pending={setPasswordAction.pending}
            testID={`${page}-submit`}
          >
            {page === "reset"
              ? AUTH_COPY.actions.changePassword
              : AUTH_COPY.actions.setPassword}
          </Button>
        </View>
      </Form>
    </AuthCard>
  );
}
